import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CopyDiscountsResult,
  type EditableLine,
  type FeeRate,
  type Semester,
  type TransferShareInput,
  type TuitionChargeKindFilter,
  type TuitionPaymentMethod,
  type TuitionPrepaid,
  tuitionApi,
} from './tuition';

export const tuitionKeys = {
  feeTypes: ['tuition', 'fee-types'] as const,
  fees: (yearId: string) => ['tuition', 'fees', yearId] as const,
  students: (yearId: string) => ['tuition', 'students', yearId] as const,
  charges: (yearId: string, semester: Semester) => ['tuition', 'charges', yearId, semester] as const,
  /** Without `kind` it is the prefix of every kind's list, which is what a payment invalidates. */
  families: (yearId: string, semester: Semester, kind?: TuitionChargeKindFilter) =>
    kind
      ? (['tuition', 'families', yearId, semester, kind] as const)
      : (['tuition', 'families', yearId, semester] as const),
};

export function useTuitionFeeTypesQuery() {
  return useQuery({ queryKey: tuitionKeys.feeTypes, queryFn: tuitionApi.feeTypes, staleTime: Infinity });
}

export function useTuitionFeesQuery(yearId: string | undefined) {
  return useQuery({
    queryKey: tuitionKeys.fees(yearId ?? ''),
    queryFn: () => tuitionApi.fees(yearId!),
    enabled: !!yearId,
  });
}

export function useReplaceTuitionFeesMutation(yearId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: FeeRate[]) => tuitionApi.replaceFees(yearId, rows),
    onSuccess: data => qc.setQueryData(tuitionKeys.fees(yearId), data),
  });
}

export function useFillPublishedFeesMutation(yearId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tuitionApi.fillPublished(yearId),
    onSuccess: data => qc.setQueryData(tuitionKeys.fees(yearId), data),
  });
}

export function useTuitionStudentsQuery(yearId: string | undefined) {
  return useQuery({
    queryKey: tuitionKeys.students(yearId ?? ''),
    queryFn: () => tuitionApi.students(yearId!),
    enabled: !!yearId,
  });
}

function useStudentsMutation<TInput, TResult = unknown>(yearId: string, fn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: tuitionKeys.students(yearId) }),
  });
}

export function useGrantTuitionDiscountMutation(yearId: string) {
  return useStudentsMutation(yearId, (input: { studentId: string; percent: number; reason: string }) =>
    tuitionApi.grantDiscount(yearId, input.studentId, { percent: input.percent, reason: input.reason })
  );
}

export function useWithdrawTuitionDiscountMutation(yearId: string) {
  return useStudentsMutation(yearId, (studentId: string) => tuitionApi.withdrawDiscount(yearId, studentId));
}

export function useCopyTuitionDiscountsMutation(yearId: string) {
  return useStudentsMutation<void, CopyDiscountsResult>(yearId, () => tuitionApi.copyDiscounts(yearId));
}

export function useSetTuitionNoteMutation(yearId: string) {
  return useStudentsMutation(yearId, (input: { studentId: string; note: string | null }) =>
    tuitionApi.setNote(yearId, input.studentId, input.note)
  );
}

export function useSetTuitionPrepaidMutation(yearId: string) {
  return useStudentsMutation(yearId, (input: TuitionPrepaid & { studentId: string }) =>
    tuitionApi.setPrepaid(input.studentId, {
      fromStartYear: input.fromStartYear,
      fromSemester: input.fromSemester,
      untilStartYear: input.untilStartYear,
      untilSemester: input.untilSemester,
      note: input.note,
    })
  );
}

export function useClearTuitionPrepaidMutation(yearId: string) {
  return useStudentsMutation(yearId, (studentId: string) => tuitionApi.clearPrepaid(studentId));
}

export function useTuitionChargesQuery(yearId: string | undefined, semester: Semester) {
  return useQuery({
    queryKey: tuitionKeys.charges(yearId ?? '', semester),
    queryFn: () => tuitionApi.charges(yearId!, semester),
    enabled: !!yearId,
  });
}

function useChargesMutation<TInput, TResult>(
  yearId: string,
  semester: Semester,
  fn: (input: TInput) => Promise<TResult>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: tuitionKeys.charges(yearId, semester) }),
  });
}

export function useGenerateTuitionChargesMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, () => tuitionApi.generate(yearId, semester));
}

export function useGenerateOneTuitionDraftMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, (studentId: string) =>
    tuitionApi.generateOne(yearId, semester, studentId)
  );
}

export function useCreateExtraTuitionChargeMutation(yearId: string, semester: Semester) {
  return useChargesMutation(
    yearId,
    semester,
    (input: { studentId: string; title: string; lines: EditableLine[]; note: string | null }) =>
      tuitionApi.createExtra(yearId, semester, input.studentId, {
        title: input.title,
        lines: input.lines,
        note: input.note,
      })
  );
}

export function useEditTuitionChargeMutation(yearId: string, semester: Semester) {
  return useChargesMutation(
    yearId,
    semester,
    (input: { id: string; lines: EditableLine[]; note: string | null; title?: string }) =>
      tuitionApi.editCharge(input.id, { lines: input.lines, note: input.note, title: input.title })
  );
}

export function useResetTuitionChargeMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, (id: string) => tuitionApi.resetCharge(id));
}

export function useIssueTuitionChargeMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, (input: { id: string; dueDate: string }) =>
    tuitionApi.issueCharge(input.id, input.dueDate)
  );
}

export function useIssueAllTuitionChargesMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, (dueDate: string) => tuitionApi.issueAll(yearId, semester, dueDate));
}

export function useCancelTuitionChargeMutation(yearId: string, semester: Semester) {
  return useChargesMutation(yearId, semester, (input: { id: string; reason: string }) =>
    tuitionApi.cancelCharge(input.id, input.reason)
  );
}

export function useTuitionFamiliesQuery(
  yearId: string | undefined,
  semester: Semester,
  kind: TuitionChargeKindFilter = 'all'
) {
  return useQuery({
    queryKey: tuitionKeys.families(yearId ?? '', semester, kind),
    queryFn: () => tuitionApi.families(yearId!, semester, kind),
    enabled: !!yearId,
  });
}

/** A payment changes what an invoice has paid, so the families and the invoices both refresh. */
function usePaymentsMutation<TInput, TResult>(
  yearId: string,
  semester: Semester,
  fn: (input: TInput) => Promise<TResult>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: tuitionKeys.families(yearId, semester) }),
        qc.invalidateQueries({ queryKey: tuitionKeys.charges(yearId, semester) }),
      ]);
    },
  });
}

export function useRecordTuitionTransferMutation(yearId: string, semester: Semester) {
  return usePaymentsMutation(
    yearId,
    semester,
    (input: { shares: TransferShareInput[]; method: TuitionPaymentMethod; receivedOn: string; note?: string }) =>
      tuitionApi.recordTransfer(input)
  );
}

export function useConfirmTuitionTransferMutation(yearId: string, semester: Semester) {
  return usePaymentsMutation(
    yearId,
    semester,
    ({
      id,
      ...input
    }: {
      id: string;
      amount: number;
      expectedAmount: number;
      shares: TransferShareInput[];
      method: TuitionPaymentMethod;
      receivedOn: string;
    }) => tuitionApi.confirmTransfer(id, input)
  );
}

export function useRejectTuitionTransferMutation(yearId: string, semester: Semester) {
  return usePaymentsMutation(yearId, semester, (input: { id: string; expectedAmount: number; note: string }) =>
    tuitionApi.rejectTransfer(input.id, { note: input.note, expectedAmount: input.expectedAmount })
  );
}

export function useCreateTuitionReceiptMutation(yearId: string, semester: Semester) {
  return usePaymentsMutation(yearId, semester, (paymentId: string) => tuitionApi.createReceipt(paymentId));
}

export function useVoidTuitionReceiptMutation(yearId: string, semester: Semester) {
  return usePaymentsMutation(yearId, semester, (input: { receiptId: string; reason: string }) =>
    tuitionApi.voidReceipt(input.receiptId, input.reason)
  );
}

export function useReceiptCopiesQuery() {
  return useQuery({ queryKey: ['tuition', 'receipt-copies'], queryFn: () => tuitionApi.receiptCopies() });
}

function useCopiesMutation(fn: (id: string) => Promise<void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tuition', 'receipt-copies'] }),
  });
}

export function useReceiptCopyReadyMutation() {
  return useCopiesMutation(id => tuitionApi.copyReady(id));
}

export function useReceiptCopyCollectedMutation() {
  return useCopiesMutation(id => tuitionApi.copyCollected(id));
}
